import type { ProductListAPIResponse } from '@partful/shared-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WhiteTextFieldWithoutLabel } from 'src/components/TextInput/WhiteTextFieldWithoutLabel';
import {
  FileDetailsRowContainer,
  FlexRow,
  Label,
  LeftColumn,
  OutlinedButton,
  RightColumn,
  RowDivider,
  TextFieldContainer,
} from '../styles';
import { ProductFile } from '../Uploader';
import { FileDetailsProductDropdown } from './FileDetailsProductDropdown';
import { FileName } from './FileName';

type FileDetailsRowProps = Readonly<{
  productFile: ProductFile;
  products: ProductListAPIResponse;
  editFileDetails: (newDetails: Partial<ProductFile>) => void;
  removeFile: () => void;
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}>;

export function FileDetailsRow({
  productFile,
  editFileDetails,
  products,
  removeFile,
  setDisabled,
}: FileDetailsRowProps) {
  const { errorMessage, validateDescription } = useDescriptionValidation();
  /* istanbul ignore next */
  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    validateDescription(event.target.value);
    editFileDetails({ description: event.target.value });
  };

  useEffect(() => {
    setDisabled(Boolean(errorMessage));
  }, [errorMessage, setDisabled]);

  return (
    <FileDetailsRowContainer>
      <FlexRow>
        <LeftColumn>
          <FileName fileName={productFile.name} removeFile={removeFile} />
        </LeftColumn>
        <RightColumn>
          <FileDetailsProductDropdown file={productFile} products={products} editFileDetails={editFileDetails} />
        </RightColumn>
      </FlexRow>
      <FlexRow>
        <FileDescription
          description={productFile.description}
          errorMessage={errorMessage}
          onChange={handleDescriptionChange}
        />
      </FlexRow>
      <RowDivider />
    </FileDetailsRowContainer>
  );
}

type FileDescriptionProps = {
  description?: string;
  errorMessage?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export function FileDescription({ description, onChange, errorMessage }: FileDescriptionProps) {
  const [showDescription, setShowDescription] = useState(Boolean(description));

  const { t } = useTranslation('uploads');

  if (showDescription) {
    return (
      <TextFieldContainer>
        <Label>{t('description')}</Label>
        <WhiteTextFieldWithoutLabel
          value={description}
          onChange={onChange}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          multiline
          placeholder={`${t('file')} ${t('description').toLowerCase()}`}
          data-testid="file-description-input"
        />
      </TextFieldContainer>
    );
  }
  return (
    <OutlinedButton onClick={() => setShowDescription(true)} data-testid="upload-modal-add-description">
      {t('addFileDescription')}
    </OutlinedButton>
  );
}

export function useDescriptionValidation() {
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation('uploads');
  const maxDescriptionLength = 1000;
  const validateDescription = /* istanbul ignore next */ (description: string) => {
    const message =
      description.length > maxDescriptionLength
        ? t('status.error.descriptionMaxCharacters', { max: maxDescriptionLength })
        : '';
    /* istanbul ignore next */
    setErrorMessage(message);
  };
  return { errorMessage, validateDescription };
}
